/**
 * Shared schedule types for API contracts
 */
import { z } from "zod";
import { PayConfigurationResponseSchema } from "./payConfiguration.js";
import { uuidSchema, dateSchema, timeHhmmSchema } from "./validation-common.js";
// Constant for time off entries in schedule
export const TIME_OFF = "TIME_OFF";
export const PeriodSchema = z.enum(["WEEK", "TWO_WEEK"]);
// Query schema for schedule endpoints
export const ScheduleQuerySchema = z.object({
    locationId: uuidSchema,
    startDate: dateSchema,
    period: PeriodSchema,
});
// Zod schemas for oRPC contracts
export const BreakSummarySchema = z.object({
    id: z.uuid(),
    startOffsetMinutes: z.number().nullable(),
    breakDurationMinutes: z.number(),
    isPaid: z.boolean(),
    description: z.string().nullish(),
    emoji: z.string().nullish(),
    shiftId: z.string().optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const ChecklistSummarySchema = z.object({
    shiftChecklistId: z.string(),
    name: z.string(),
    checklistTemplateId: z.string().optional(),
    checklistCompleted: z.boolean(),
});
export const WarningSchema = z.object({
    severity: z.enum(["LOW", "HIGH"]),
    message: z.string(),
});
export const ShiftSummarySchema = z.object({
    shiftId: z.uuid(),
    locationId: z.string(), // Can be "" for time off entries
    employeeId: z.uuid().optional(),
    approved: z.boolean(),
    jobRoleIds: z.array(z.uuid()),
    jobRoles: z.array(z.object({
        id: z.uuid(),
        name: z.string(),
        jobRoleFamilyId: z.uuid(),
        payConfiguration: PayConfigurationResponseSchema.optional(),
    })),
    jobRoleFamilyId: z.string(), // Can be "TIME_OFF" for time off entries
    jobName: z.string(),
    jobRoleFamilyColor: z.string(),
    shiftStart: z.iso.datetime(),
    shiftEnd: z.iso.datetime(),
    breaks: z.array(BreakSummarySchema),
    checklists: z.array(ChecklistSummarySchema),
    warnings: z.array(WarningSchema),
    shiftLaborCost: z.number(),
    isTimeOff: z.boolean(),
    status: z.enum(["DRAFT", "PUBLISHED"]),
    parentShiftId: z.uuid().optional(),
    effectivePayRate: z.number().optional(),
    effectiveCompensationType: z.enum(["SALARIED", "HOURLY"]).optional(),
    locationName: z.string().optional(),
});
export const SchedulePreferenceSummarySchema = z.object({
    availability: z.enum(["AVAILABLE", "PREFERRED", "UNAVAILABLE"]),
    availabilityStart: timeHhmmSchema,
    availabilityEnd: timeHhmmSchema,
    dayOfWeek: z.string(),
});
export const EmployeeScheduleSchema = z.object({
    employeeId: z.uuid(),
    employeeName: z.string(),
    employeeMinWeeklyHours: z.number(),
    employeeDesiredHours: z.number(),
    employeeJobRoleFamilies: z.array(z.string()),
    shifts: z.array(ShiftSummarySchema),
    schedulePreferences: z.array(SchedulePreferenceSummarySchema),
});
export const LaborForecastDataPointSchema = z.object({
    timestamp: z.iso.datetime(),
    value: z.number(),
    lowerBound: z.number().nullable(),
    upperBound: z.number().nullable(),
});
export const SalesForecastDataPointSchema = z.object({
    timestamp: z.iso.datetime(),
    value: z.number(),
    lowerBound: z.number().nullable(),
    upperBound: z.number().nullable(),
});
export const JobRoleFamilySummarySchema = z.object({
    jobRoleFamilyId: z.uuid(),
    jobRoleFamilyName: z.string(),
    jobRoleFamilyColor: z.string(),
    isArchived: z.boolean(),
    laborForecasts: z.array(LaborForecastDataPointSchema).optional(),
});
export const ScheduleMetricsSchema = z.object({
    totalScheduledHours: z.number(),
    totalLaborCost: z.number(),
    hoursByEmployee: z.record(z.string(), z.object({
        employeeName: z.string(),
        totalHours: z.number(),
        desiredHours: z.number(),
        variance: z.number(),
    })),
    costsByEmployee: z.record(z.string(), z.object({
        employeeName: z.string(),
        totalCost: z.number(),
        totalHours: z.number(),
        averageHourlyRate: z.number(),
    })),
    hoursByJobRole: z.record(z.string(), z.object({
        jobRoleName: z.string(),
        jobRoleFamilyId: z.string(),
        totalHours: z.number(),
    })),
    costsByJobRole: z.record(z.string(), z.object({
        jobRoleName: z.string(),
        jobRoleFamilyId: z.string(),
        totalCost: z.number(),
        totalHours: z.number(),
        averageHourlyRate: z.number(),
    })),
});
export const ScheduleResponseSchema = z.object({
    locationId: z.uuid(),
    startDate: z.string(),
    locationTimezone: z.string(),
    period: PeriodSchema,
    locationEmployees: z.array(EmployeeScheduleSchema),
    locationJobRoleFamilies: z.array(JobRoleFamilySummarySchema),
    openShifts: z.array(ShiftSummarySchema),
    salesForecasts: z.array(SalesForecastDataPointSchema).optional(),
    computedMetrics: ScheduleMetricsSchema.optional(),
});
export const SalesDataStubResponseSchema = z.object({
    data: z.object({
        salesData: z.array(z.unknown()),
        summary: z.object({
            totalSales: z.number(),
            totalTransactions: z.number(),
            averageTransactionValue: z.number(),
            totalTips: z.number(),
            averageTipValue: z.number(),
            tipPercentage: z.number(),
        }),
    }),
    metadata: z.object({
        dataComplete: z.boolean(),
        lastSync: z.string().nullable(),
        dateRange: z.object({
            start: z.iso.datetime(),
            end: z.iso.datetime(),
        }),
        totalRecords: z.number(),
        isStub: z.boolean(),
        message: z.string(),
    }),
});
